import React, { useEffect } from "react";
import { Redirect, Route, Router, Switch } from "react-router";
import { createBrowserHistory } from "history";
import { connect, useSelector } from "react-redux";
import ConsultationFlow from "main/ConsultationFlow";
import PatientSearchContainer from "main/PatientSearch/PatientSearchContainer";
import { GlobalState } from "store/configure-store";
import {
  selectPractice,
  selectUser,
} from "store/reducers/user-context.reducer";
import { Config, visitConfig } from "config";
import LoginCallback from "main/Login/LoginCallback";
import LoginHome from "main/Login/LoginHome";
import { i18nUserContext } from "config/translations/I18n";
import { User } from "model/user.model";
import NotificationModal from "main/Notification/NotificationModal";
import { selectNotificationType } from "store/reducers/notification.reducer";
import { NotificationTypeEnum } from "common/enums/NotificationTypeEnum";
import { resetNotification } from "store/actions/notification.actions";
import Tutorial from "main/Tutorial/Tutorial";
import { isLoggedIn } from "store/reducers/session.reducer";
import "@cc/bootstrap-theme/css/cc.bootstrap.min.css";
import { useTranslation } from "react-i18next";
import Page from "@cc/react-components/Page";
import BlockingMessage from "ui/BlockingMessage";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { shouldShowHelenaUserActivationPopup } from "store/reducers/helena.reducer";
import HelenaUserActivation from "./main/HelenaInvite/HelenaUserActivation";
import { useMediaQuery } from "@react-hooks-hub/use-media-query";

const browserHistory = createBrowserHistory();

interface Props {
  isLoggedIn: boolean;
  user: User;
  practice: string;
  visitConfig: Config;
  notificationType: NotificationTypeEnum;
}

interface DispatchToPropsState {
  resetNotification: (keepNotification: boolean) => void;
}

const App = ({
  isLoggedIn,
  user,
  practice,
  notificationType,
  resetNotification,
}: DispatchToPropsState & Props) => {
  const { pushInstruction } = useMatomo();
  const { orientation } = useMediaQuery();
  const showHelenaUserActivationPopup = useSelector(
    shouldShowHelenaUserActivationPopup
  );

  useEffect(() => {
    if (isLoggedIn) {
      user && user.language && i18nUserContext.changeLanguage(user.language);
      const userId = [practice, user.username]
        .filter((value) => !!value)
        .join("/");
      pushInstruction("setUserId", userId);
    }
  }, [isLoggedIn, user, practice, pushInstruction]);

  useEffect(() => {
    const h =
      Math.max(document.documentElement.clientHeight, window.innerHeight || 0) *
      0.01;
    document.documentElement.style.setProperty("--vh", `${h}px`);
  }, []);

  const renderNotificationModal = () => {
    return (
      <NotificationModal
        show={notificationType !== NotificationTypeEnum.NOTHING}
        setShow={resetNotification}
        notificationType={notificationType}
      />
    );
  };

  const [t] = useTranslation();

  const getLoginCallbackUrl = () => {
    if (showTutorial()) {
      return "/tutorial";
    } else {
      return "/";
    }
  };

  return (
    <>
      {orientation === "landscape" && (
        <Page>
          <Page.Body>
            <BlockingMessage
              message={t("app.rotateDeviceMessage")}
              title={t("app.rotateDeviceTitle")}
              iconType="alert"
            />
          </Page.Body>
        </Page>
      )}
      {orientation === "portrait" && (
        <Router history={browserHistory}>
          {renderNotificationModal()}
          {isLoggedIn ? (
            <Switch>
              {showHelenaUserActivationPopup && (
                <Route path="/" component={HelenaUserActivation} />
              )}
              <Route path="/" exact component={PatientSearchContainer} />
              <Route path="/consultation" component={ConsultationFlow} />
              <Route path="/tutorial" component={Tutorial} />
              <Redirect
                push={false}
                from={"/auth/login/passport/callback"}
                to={getLoginCallbackUrl()}
              />
              <Redirect
                push={false}
                from={"/dev/login"}
                to={getLoginCallbackUrl()}
              />
            </Switch>
          ) : (
            <Switch>
              <Route
                exact
                path={"/auth/login/passport/callback"}
                component={LoginCallback}
              />
              <Route
                path="/"
                render={(props) => (
                  <LoginHome {...props} config={visitConfig} />
                )}
              />
            </Switch>
          )}
        </Router>
      )}
    </>
  );
};

const showTutorial = (): boolean => {
  return localStorage.getItem("showTutorial") !== "false";
};

export const updateShowTutorial = () => {
  localStorage.setItem("showTutorial", "false");
};

const mapStateToProps = (state: GlobalState) => {
  return {
    isLoggedIn: isLoggedIn(state),
    user: selectUser(state),
    practice: selectPractice(state),
    notificationType: selectNotificationType(state),
  };
};

export default connect(mapStateToProps, { resetNotification })(App);
